import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AangeverBurgerlijkeStaat,
  AangeverSamenwoonSituatie,
  Aangifte, BokkzAlgDataService,
  BokkzIbDataService,
  IncomeTaxEntity,
  Kind
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {Subject, takeUntil} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-kind',
  templateUrl: './kind.component.html',
  styleUrls: ['./kind.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiAlertModule,
        SharedUiDetailsModule,
        CommonModule,
        SharedUiDialogModule
    ]
})
export class KindComponent implements OnInit, OnDestroy {

  @Input() kindlijst = {} as KindComponent;
  @Input() public id!: string;
  protected readonly AangeverBurgerlijkeStaat = AangeverBurgerlijkeStaat;
  protected readonly AangeverSamenwoonSituatie = AangeverSamenwoonSituatie;
  private destroy$ = new Subject<void>();
  private clientId!: string;
  private focusedElementId: string | null = null;
  public incomeTaxEntity = {} as IncomeTaxEntity;
  public kind = {} as FormGroup;
  public hasFiscalPartner = false;
  public dialogVisible = false;
    public index = 0;

  public itemsJaNee: SelectFormControlItem<boolean | undefined>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  constructor(public fb: FormBuilder,
              public dataService: BokkzIbDataService,
              public dataAlgService: BokkzAlgDataService) {
    this.kind = this.fb.group({
      kinderen: this.fb.array<Kind>([]),
    });
  }

    showDialog(index: number) {
        this.index = index;
        this.dialogVisible = true;
    }

  public ngOnInit(): void {
    this.dataService.incomeTaxEntity.pipe(takeUntil(this.destroy$)).subscribe((incomeTaxEntity) => {
      this.incomeTaxEntity = incomeTaxEntity;
      if (incomeTaxEntity.aangifte) {
        this.updateForm();
      }
    });
    this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
      this.clientId = client.sstRelationId!;
    })
  }

  ngOnDestroy(): void {
    if (this.incomeTaxEntity?.sstRelationId) {
      this.dataService.postIncomeTaxEntity(
        this.incomeTaxEntity.sstRelationId,
        false,
        this.incomeTaxEntity
      );
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get deadline() {
    const aangifteJaar = this.incomeTaxEntity?.aangifte?.aangifteJaar;
    return new Date(`${aangifteJaar}-07-01`);
  }

  hasKindOuderDan18(): boolean {
      const aangifteJaar = this.incomeTaxEntity?.aangifte?.aangifteJaar;
      if (!aangifteJaar) {
          return false;
      }
      return this.kinderen.controls.some(control => {
          const geboortedatum = control.get('geboortedatum')?.value;
          if (!geboortedatum) return false;

          const geboorteDatum = new Date(geboortedatum);

          // Kind is 18 or older on 31-12 of the tax year
          const peilDatum = new Date(aangifteJaar, 11, 31); // 31 December

          let leeftijd = peilDatum.getFullYear() - geboorteDatum.getFullYear();

          const verjaardagNogNietGeweest =
              peilDatum.getMonth() < geboorteDatum.getMonth() ||
              (peilDatum.getMonth() === geboorteDatum.getMonth() &&
                  peilDatum.getDate() < geboorteDatum.getDate());

          if (verjaardagNogNietGeweest) {
              leeftijd--;
          }

          return leeftijd >= 18;
      });
  }

  get kinderen(): FormArray {
    return this.kind.get('kinderen') as FormArray;
  }

  addRijKinderen(): void {
    this.kinderen.push(this.createKindFormGroup());
  }

  verwijderRijKinderen(index: number): void {
    this.kinderen.removeAt(index);
    this.kind.updateValueAndValidity()
    this.kind.markAsDirty()
    this.saveForm();
  }

  createKindFormGroup(kind?: Kind): FormGroup {
    return this.fb.group<Kind>({
      voornaam: kind ? kind.voornaam : '',
      geboortedatum: kind ? kind.geboortedatum : null as unknown as Date,
      datumOverlijden: kind ? kind.datumOverlijden : null as unknown as Date,
      bsn: kind ? kind.bsn : '',
      ingeschrevenOpAdresAangever: kind ? kind.ingeschrevenOpAdresAangever : (undefined as unknown as boolean),
      ingeschrevenOpAdresPartner: kind ? kind.ingeschrevenOpAdresPartner : (undefined as unknown as boolean),
      bijExWoonAdres: kind ? kind.bijExWoonAdres : (undefined as unknown as boolean),
      coOuderschap: kind ? kind.coOuderschap : (undefined as unknown as boolean),
      tenMinsteDrieDagenPerWeekWoonachtig: kind ? kind.tenMinsteDrieDagenPerWeekWoonachtig : (undefined as unknown as boolean),
      ouderOntvangtKinderbijslag: kind ? kind.ouderOntvangtKinderbijslag : (undefined as unknown as boolean),
      onderhoudBelangrijkeMate: kind ? kind.onderhoudBelangrijkeMate : (undefined as unknown as boolean),
    });
  }

  private updateForm(): void {
    const kinderen = this.incomeTaxEntity.aangifte?.kinderen || [];
    const formArray = this.kind.get('kinderen') as FormArray;
    const focusedControl = this.focusedElementId
      ? (document.getElementById(this.focusedElementId) as HTMLElement)
      : null;

    while (formArray.length > kinderen.length) {
      formArray.removeAt(formArray.length - 1);
    }

    // Add or update controls
    for (let i = 0; i < kinderen.length; i++) {
      if (i < formArray.length) {
        // Update existing control
        const control = formArray.at(i) as FormGroup;
        control.patchValue(kinderen[i]);
      } else {
        // Add new control
        const formGroup = this.createKindFormGroup(kinderen[i]);
        formArray.push(formGroup);
      }
    }

    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }
  }

  public saveForm(): void {
    this.kind.updateValueAndValidity();
    setTimeout(() => {
      this.incomeTaxEntity.aangifte = {
        ...this.incomeTaxEntity.aangifte,
        kinderen: this.kind.value.kinderen
      } as Aangifte;
      this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
    }, 20)
  }

    get heeftPeriodeVanMinimaal6Maanden(): boolean {
        const periode = this.incomeTaxEntity?.aangifte?.aangever?.relatiegegevens?.[0]
            ?.periodeIngeschrevenOpZelfdeAdres;
        if (!periode?.begindatum || !periode?.einddatum) return false;

        const begin = new Date(periode.begindatum);
        const eind = new Date(periode.einddatum);

        const maanden =
            (eind.getFullYear() - begin.getFullYear()) * 12 +
            (eind.getMonth() - begin.getMonth());

        return maanden >= 6;
    }
}
